/**
 * Backend API Client for Storyboard Processing
 */
import BACKEND_CONFIG from '../config/backendConfig';

/**
 * Parse storyboard XML using Python backend
 * @param {string} xmlContent - Storyboard XML content
 * @returns {Promise<object>} Parsed storyboard data
 */
export const parseStoryboardWithBackend = async (xmlContent) => {
  try {
    const response = await fetch(`${BACKEND_CONFIG.API_URL}/api/parse-storyboard`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        xml_content: xmlContent,
      }),
      signal: AbortSignal.timeout(BACKEND_CONFIG.TIMEOUT),
    });

    if (!response.ok) {
      throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - backend service may be unavailable');
    }
    throw error;
  }
};

/**
 * Analyze constraints using Python backend
 * @param {string} xmlContent - Storyboard XML content
 * @returns {Promise<object>} Constraint analysis data
 */
export const analyzeConstraintsWithBackend = async (xmlContent) => {
  try {
    const response = await fetch(`${BACKEND_CONFIG.API_URL}/api/analyze-constraints`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        xml_content: xmlContent,
      }),
      signal: AbortSignal.timeout(BACKEND_CONFIG.TIMEOUT),
    });

    if (!response.ok) {
      throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      constraints: data.constraints,
      hierarchy: data.hierarchy,
    };
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - backend service may be unavailable');
    }
    throw error;
  }
};

/**
 * Extract styles using Python backend
 * @param {string} xmlContent - Storyboard XML content
 * @returns {Promise<object>} Extracted styles data
 */
export const extractStylesWithBackend = async (xmlContent) => {
  try {
    const response = await fetch(`${BACKEND_CONFIG.API_URL}/api/extract-styles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        xml_content: xmlContent,
      }),
      signal: AbortSignal.timeout(BACKEND_CONFIG.TIMEOUT),
    });

    if (!response.ok) {
      throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.styles;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - backend service may be unavailable');
    }
    throw error;
  }
};

/**
 * Generate SwiftUI code using Python backend (deterministic conversion)
 * @param {object} parsedData - Parsed storyboard data
 * @param {string} sceneXml - Scene XML content
 * @param {string} sceneId - Optional scene ID
 * @returns {Promise<string>} Generated SwiftUI code
 */
export const generateSwiftUIWithBackend = async (parsedData, sceneXml, sceneId = null) => {
  try {
    const response = await fetch(`${BACKEND_CONFIG.API_URL}/api/generate-swiftui`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        parsed_data: parsedData,
        scene_xml: sceneXml,
        scene_id: sceneId,
      }),
      signal: AbortSignal.timeout(BACKEND_CONFIG.TIMEOUT * 2), // Longer timeout for generation
    });

    if (!response.ok) {
      throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.code;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - code generation took too long');
    }
    throw error;
  }
};

/**
 * Check if backend is available
 * @returns {Promise<boolean>} True if backend is available
 */
export const checkBackendAvailability = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
    
    const response = await fetch(`${BACKEND_CONFIG.API_URL}/`, {
      method: 'GET',
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    // Backend not available - this is fine, we'll use direct API
    console.log('Backend not available, using direct API:', error.message);
    return false;
  }
};
